import React, { Component, ErrorInfo } from 'react';
import { tr } from '../../i18n/i18n';
import { isChunkLoadError } from '../../utility/lazyRetry';
import { getHomeRoute } from '../../utility/navigationHelper';
import { SESSION_STORAGE_KEY } from '../../constant/auth.constant';
import AppButton from '../atoms/AppButton/AppButton';
import { somethingWentWrongImage } from '../../assets/images';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global ErrorBoundary — class-based error boundary for the entire app.
 *
 * This wraps the App component to catch errors that occur outside of
 * React Router's error boundary (e.g., errors in providers, context, etc.).
 *
 * For chunk load errors, it auto-reloads the page once.
 * For other errors, it shows a fallback UI using project styling standards.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Handle chunk load errors by reloading
    if (isChunkLoadError(error)) {
      const hasReloaded = sessionStorage.getItem(SESSION_STORAGE_KEY.GLOBAL_ERROR_BOUNDARY_RELOADED);

      if (!hasReloaded) {
        sessionStorage.setItem(SESSION_STORAGE_KEY.GLOBAL_ERROR_BOUNDARY_RELOADED, 'true');
        window.location.reload();
        return;
      }

      // Already reloaded — clear flag, let fallback render
      sessionStorage.removeItem(SESSION_STORAGE_KEY.GLOBAL_ERROR_BOUNDARY_RELOADED);
    }

    // Log error for debugging (could integrate with Sentry or similar)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = (): void => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY.GLOBAL_ERROR_BOUNDARY_RELOADED);
    window.location.reload();
  };

  handleGoHome = (): void => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY.GLOBAL_ERROR_BOUNDARY_RELOADED);
    window.location.href = getHomeRoute();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { t } = useTranslation();
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100 p-6 text-center">
          <div className="bg-white p-12 rounded-xl shadow-lg max-w-md w-full flex flex-col items-center gap-4">
            <img
              src={somethingWentWrongImage}
              alt={tr('common.alt_something_wrong', 'Something went wrong')}
              className="w-48 h-auto"
            />
            <h1 className="text-3xl font-bold text-project-purple m-0">{t('g.oops', 'Oops!')}</h1>
            <h2 className="text-lg font-semibold text-gray-800 m-0">{t('g.something_went_wrong', 'Something Went Wrong')}</h2>
            <p className="text-sm text-gray-500 leading-relaxed m-0">{t('g.an_unexpected_error_occurred_please_try_refreshing_the_', 'An unexpected error occurred. Please try refreshing the page.')}</p>
            <div className="flex flex-col gap-3 w-full mt-2">
              <AppButton
                label={t('common.button_refresh_page', 'Refresh Page')}
                onClick={this.handleReload}
                className="w-full"
              />
              <AppButton
                label={t('common.button_go_to_home', 'Go to Home')}
                onClick={this.handleGoHome}
                className="p-button-outlined w-full"
              />
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
